import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Notice } from '../../components/ui';
import { relativeDate } from '../../lib/school';
import type { BuildStage, BuildTest, FrameworkBuild, FrameworkRequest } from '../../lib/frameworks';
import { LeaderMessagePreview } from './LeaderMessagePreview';

const STEPS: { stage: BuildStage[]; label: string }[] = [
  { stage: ['queued', 'reading'], label: 'Reading' },
  { stage: ['building'], label: 'Building' },
  { stage: ['testing'], label: 'Testing on sample lessons' },
  { stage: ['checking'], label: 'Checking' },
];

export const isBuilding = (b: FrameworkBuild | null | undefined) => !!b && !['ready', 'needs_info', 'failed'].includes(b.stage);

const QUALITY: Record<BuildTest['quality'], string> = { strong: 'Strong lesson', typical: 'Typical lesson', weak: 'Weak lesson' };

type Props = {
  request: FrameworkRequest;
  busy: boolean;
  onBuild: () => void;
  onPublish: () => void;
  onAsk: (note: string) => void;
};

/**
 * The AI builder's work on a request: progress while it runs, then the
 * scorecard, the framework with where each indicator came from, what was
 * left out and why, how it scored three sample lessons, and the admin's
 * choices (publish, edit, rebuild, or ask the leader for a bit more).
 */
export function FrameworkBuildReview({ request: r, busy, onBuild, onPublish, onAsk }: Props) {
  const b = r.build ?? null;

  if (!b) {
    return (
      <div className="build panel-tint build-empty">
        <div>
          <h3>Build it with AI</h3>
          <p className="muted">Reads what they sent, builds a draft to the rules, tests it on three sample lessons and checks it. Takes a minute or two.</p>
        </div>
        <Button onClick={onBuild} loading={busy}>
          Build with AI
        </Button>
      </div>
    );
  }

  if (isBuilding(b)) {
    const current = STEPS.findIndex((s) => s.stage.includes(b.stage));
    return (
      <div className="build">
        <h3>Building{b.round > 1 ? ` · round ${b.round} of 3` : ''}</h3>
        <ol className="build-steps">
          {STEPS.map((s, i) => (
            <li key={s.label} data-state={i < current ? 'done' : i === current ? 'now' : 'next'}>
              <span className="build-dot" aria-hidden="true" />
              {s.label}
            </li>
          ))}
        </ol>
        {b.round > 1 ? <p className="muted">The checker sent the draft back with fixes. This page updates by itself.</p> : null}
      </div>
    );
  }

  // Stopped before a draft was built and tested: nothing to review yet.
  if (b.stage === 'failed' && (!b.frameworkId || !b.scorecard.length)) {
    return (
      <div className="build">
        <Notice tone="error">The build didn&rsquo;t finish: {b.error ?? 'something went wrong.'}</Notice>
        <div className="form-actions">
          <Button onClick={onBuild} loading={busy}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (!b.frameworkId) {
    // The builder couldn't make a framework from what was sent.
    return (
      <div className="build">
        <h3>It needs a bit more</h3>
        {b.summary ? <p>{b.summary}</p> : null}
        {r.status === 'needs_info' ? (
          <Notice tone="info">You&rsquo;ve asked the leader. It builds again when they add more.</Notice>
        ) : (
          <AskLeader request={r} questions={b.questions} busy={busy} onAsk={onAsk} startOpen />
        )}
      </div>
    );
  }

  const passed = b.scorecard.filter((c) => c.pass).length;
  const published = r.status === 'ready' && r.frameworkId === b.frameworkId;
  const areas = groupByArea(b.sourceMap);

  return (
    <div className="build">
      <div className="build-head">
        <div>
          <h3>{b.frameworkName ?? 'Built framework'}</h3>
          <p className="build-meta">
            {passed} of {b.scorecard.length} checks passed · {b.round === 1 ? 'first try' : `${b.round} rounds`} · ${b.costUsd.toFixed(2)}
            {b.finishedAt ? ` · ${relativeDate(b.finishedAt).toLowerCase()}` : ''}
          </p>
        </div>
        {b.stage === 'failed' ? <span className="chip">Stopped early</span> : null}
      </div>
      {b.summary ? <p>{b.summary}</p> : null}
      {b.stage === 'failed' && b.error ? (
        <Notice tone="error">The last round didn&rsquo;t finish ({b.error}). This is the draft from the round before.</Notice>
      ) : null}

      {published ? (
        <Notice tone="success">Published. {r.schoolName ?? 'The organisation'} has it and the leader has been told.</Notice>
      ) : (
        <div className="form-actions">
          <Button onClick={onPublish} loading={busy}>
            Publish to {r.schoolName ?? 'the organisation'}
          </Button>
          <Link className="btn btn-secondary btn-md" to={`/admin/frameworks/${b.frameworkId}`}>
            Open in editor
          </Link>
          <Button variant="ghost" onClick={onBuild} disabled={busy}>
            Rebuild
          </Button>
        </div>
      )}
      {published ? null : (
        <details className="req-fold">
          <summary>What {r.requestedBy ?? 'the leader'} gets when you publish</summary>
          <div className="req-fold-body">
            <LeaderMessagePreview requestId={r.id} status="ready" who={r.requestedBy ?? 'the leader'} />
          </div>
        </details>
      )}

      <section className="build-section">
        <h4>Checks</h4>
        <ul className="checks">
          {b.scorecard.map((c) => (
            <li key={c.key} className="check" data-pass={c.pass}>
              <span className="check-mark" aria-label={c.pass ? 'Passed' : 'Needs a look'}>
                {c.pass ? <Tick /> : '!'}
              </span>
              <div>
                <p className="check-label">{c.label}</p>
                {c.note ? <p className="check-note">{c.note}</p> : null}
                {!c.pass && c.indicators.length ? (
                  <div className="chips">
                    {c.indicators.map((n) => (
                      <span key={n} className="chip">
                        {n}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="build-section">
        <h4>The framework, and where each part came from</h4>
        {published ? null : <p className="muted">Showing it as built. Changes made in the editor since won&rsquo;t show here.</p>}
        {areas.map(([area, items]) => (
          <div key={area} className="build-area">
            <p className="build-area-name">{area}</p>
            {items.map((i) => (
              <div key={i.indicator} className="build-indicator">
                <p className="build-indicator-name">{i.indicator}</p>
                {i.description ? <p>{i.description}</p> : null}
                {i.source ? <p className="build-source">From: {i.source}</p> : null}
              </div>
            ))}
          </div>
        ))}
      </section>

      {b.notIncluded.length ? (
        <section className="build-section">
          <h4>Not included, and why</h4>
          <ul className="build-list">
            {b.notIncluded.map((n) => (
              <li key={n.item}>
                <strong>{n.item}</strong>
                <span className="muted"> {n.reason}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {b.tests.length ? (
        <section className="build-section">
          <h4>Sample lessons</h4>
          <div className="test-grid">
            {b.tests.map((t) => (
              <TestCard key={t.sampleId} test={t} />
            ))}
          </div>
        </section>
      ) : null}

      {!published && r.status !== 'needs_info' ? <AskLeader request={r} questions={b.questions} busy={busy} onAsk={onAsk} /> : null}
      {r.status === 'needs_info' ? <Notice tone="info">You&rsquo;ve asked the leader for a bit more. It builds again when they add it.</Notice> : null}
    </div>
  );
}

function TestCard({ test: t }: { test: BuildTest }) {
  return (
    <details className="test-card">
      <summary>
        <span className="test-quality">{QUALITY[t.quality]}</span>
        <span className="test-score">{t.overallScore ?? '–'}</span>
        <span className="test-sub">
          {t.subject}, {t.yearGroup}
        </span>
      </summary>
      {t.error ? (
        <p className="muted">Couldn&rsquo;t be scored: {t.error}</p>
      ) : (
        <ul className="test-indicators">
          {t.indicators.map((i) => (
            <li key={i.name}>
              <div className="test-row">
                <span>{i.name}</span>
                <span className="test-row-score">{i.score ?? (i.notSeen ? 'Not seen' : 'Not scored')}</span>
              </div>
              {i.quote ? <p className="build-source">&ldquo;{i.quote}&rdquo;</p> : null}
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}

function AskLeader({
  request: r,
  questions,
  busy,
  onAsk,
  startOpen = false,
}: {
  request: FrameworkRequest;
  questions: string[];
  busy: boolean;
  onAsk: (note: string) => void;
  startOpen?: boolean;
}) {
  const [open, setOpen] = useState(startOpen);
  const [note, setNote] = useState(questions.join('\n'));
  if (!open) {
    return (
      <div className="form-actions">
        <Button variant="secondary" onClick={() => setOpen(true)}>
          Ask the leader for a bit more
        </Button>
      </div>
    );
  }
  return (
    <div className="field">
      <label htmlFor="ask-note">What we need from them</label>
      <textarea id="ask-note" className="textarea" value={note} onChange={(e) => setNote(e.target.value)} />
      <p className="field-hint">It builds again when they add it.</p>
      <LeaderMessagePreview requestId={r.id} status="needs_info" note={note} who={r.requestedBy ?? 'the leader'} />
      <div className="form-actions">
        <Button onClick={() => onAsk(note.trim())} disabled={!note.trim() || busy} loading={busy}>
          Send
        </Button>
        {startOpen ? null : (
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}

function groupByArea(map: FrameworkBuild['sourceMap']): [string, FrameworkBuild['sourceMap']][] {
  const out = new Map<string, FrameworkBuild['sourceMap']>();
  for (const row of map) out.set(row.area, [...(out.get(row.area) ?? []), row]);
  return [...out.entries()];
}

function Tick() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
      <path d="M2.5 6.2 5 8.6l4.5-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
